﻿// Adapters are registered by the widget groups in the same webpack delivery.
// Unknown components (including unrelated CMS components) are left untouched.
export function createRuntime({ document, fetch: fetchRequest, reportError = () => {} }) {
    const registry = new Map(), instances = new Map(), dependencies = new Map();
    let observer;

    function cleanupState(state, element) {
        state.controller.abort();
        state.cleanups.splice(0).reverse().forEach(cleanup => {
            try { cleanup(); } catch (error) { reportError(error, element); }
        });
    }

    function dispose(element) {
        const state = instances.get(element);
        if (!state) return;
        instances.delete(element);
        cleanupState(state, element);
    }

    function loadDependency(key, url, isReady = () => false) {
        if (isReady()) return Promise.resolve();
        if (dependencies.has(key)) return dependencies.get(key);
        const promise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => { script.remove(); reject(new Error('Dependency could not be loaded: ' + key)); };
            document.head.appendChild(script);
        });
        dependencies.set(key, promise);
        promise.catch(() => dependencies.delete(key));
        return promise;
    }

    function start(element) {
        const name = element.getAttribute('data-component');
        const adapter = registry.get(name);
        if (!adapter) return Promise.resolve();
        // Only declared inputs are part of the fingerprint, never generated rows/state.
        const inputs = adapter.attributes || [];
        const fingerprint = JSON.stringify([name, ...inputs.map(input => element.getAttribute(input))]);
        const previous = instances.get(element);
        if (previous && previous.fingerprint === fingerprint) return previous.promise;
        dispose(element);
        const state = { fingerprint, controller: new AbortController(), cleanups: [], promise: null };
        instances.set(element, state);
        const onCleanup = cleanup => {
            if (typeof cleanup !== 'function') return;
            if (state.controller.signal.aborted) cleanup();
            else state.cleanups.push(cleanup);
        };
        const context = {
            signal: state.controller.signal,
            onCleanup,
            scan,
            loadDependency,
            async request(url, options = {}) {
                return fetchRequest(url, { ...options, credentials: 'same-origin', signal: state.controller.signal });
            },
            listen(target, event, handler, options) {
                target.addEventListener(event, handler, options);
                onCleanup(() => target.removeEventListener(event, handler, options));
            },
            async getJson(url) {
                const response = await fetchRequest(url, { credentials: 'same-origin', signal: state.controller.signal });
                if (!response.ok) { const error = new Error('Data request failed (' + response.status + ')'); error.status = response.status; throw error; }
                return response.json();
            }
        };
        state.promise = Promise.resolve().then(() => {
            if (!context.signal.aborted) return adapter.init(element, context);
        }).then(onCleanup).catch(error => {
            if (!context.signal.aborted) {
                // Keep the failed fingerprint to avoid automatic retry storms, but
                // release the failed instance's requests and listeners immediately.
                cleanupState(state, element);
                try { adapter.onError?.(element, error); } finally { reportError(error, element); }
            }
        });
        return state.promise;
    }

    function scan(root = document) {
        const elements = [];
        if (root.matches?.('[data-component]')) elements.push(root);
        if (root.querySelectorAll) elements.push(...root.querySelectorAll('[data-component]'));
        return Promise.allSettled(elements.map(start));
    }

    function observe(root, Observer) {
        observer?.disconnect();
        observer = new Observer(records => {
            // Removal cleanup runs after moves, so moving an existing instance is harmless.
            for (const element of instances.keys()) if (!root.contains(element)) dispose(element);
            const changed = new Set();
            records.forEach(record => {
                if (record.type === 'childList') record.addedNodes.forEach(node => changed.add(node));
                else {
                    const adapter = registry.get(record.target.getAttribute('data-component'));
                    if (record.attributeName === 'data-component' || adapter?.attributes?.includes(record.attributeName)) {
                        // Removing/changing a type must also dispose the old adapter.
                        if (!adapter) dispose(record.target);
                        changed.add(record.target);
                    }
                }
            });
            changed.forEach(node => { if (root.contains(node)) scan(node); });
        });
        observer.observe(root, { childList: true, subtree: true, attributes: true });
    }

    return {
        scan, dispose, loadDependency, observe,
        register(name, adapter) {
            if (registry.has(name)) throw new Error('Widget already registered: ' + name);
            if (typeof adapter.init !== 'function') throw new Error('Widget requires init: ' + name);
            registry.set(name, adapter);
            return scan();
        },
        stop() { observer?.disconnect(); [...instances.keys()].forEach(dispose); }
    };
}

export function installBrowserRuntime(window) {
    if (window.BookhusWidgets) return window.BookhusWidgets;
    const runtime = createRuntime({
        document: window.document,
        fetch: window.fetch.bind(window),
        reportError: (error, element) => window.console.error('Bookhus widget', element.getAttribute('data-component'), error)
    });
    window.BookhusWidgets = runtime;
    const start = () => {
        runtime.scan();
        if (window.document.body) runtime.observe(window.document.body, window.MutationObserver);
    };
    if (window.document.readyState === 'loading') window.document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
    return runtime;
}
